/**
 * Boli Live - Store Page
 *
 * eBay-style seller storefront with products and auctions tabs.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Instagram, Facebook, MessageCircle, FileText, Truck, CreditCard } from 'lucide-react';
import { getStoreService } from '@boli/core';
import type { Store, Product, Auction } from '@boli/types';
import { StoreHeader, StoreHeaderSkeleton } from '../components/StoreHeader';
import { ProductCard, ProductCardSkeleton } from '../components/ProductCard';
import { AuctionCard, AuctionCardSkeleton } from '../components/AuctionCard';

type TabType = 'products' | 'auctions' | 'about';

export default function StorePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [store, setStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [auctions, setAuctions] = useState<Partial<Auction>[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('products');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch store data
  useEffect(() => {
    const fetchStore = async () => {
      if (!slug) return;

      setIsLoading(true);
      setError(null);

      try {
        const storeService = getStoreService();
        const fetchedStore = await storeService.getStore(slug);

        if (!fetchedStore) {
          setError('Store not found');
          setIsLoading(false);
          return;
        }

        setStore(fetchedStore);
      } catch {
        setError('Failed to load store');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStore();
  }, [slug]);

  // Fetch tab content
  useEffect(() => {
    const fetchTabContent = async () => {
      if (!slug || !store) return;

      setIsLoadingContent(true);

      try {
        const storeService = getStoreService();

        if (activeTab === 'products') {
          const result = await storeService.getStoreProducts(slug, { limit: 50 });
          setProducts(result.products);
        } else if (activeTab === 'auctions') {
          const result = await storeService.getStoreAuctions(slug, { limit: 50 });
          setAuctions(result.auctions);
        }
      } catch {
        // Failed to fetch tab content
      } finally {
        setIsLoadingContent(false);
      }
    };

    fetchTabContent();
  }, [slug, store, activeTab]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <StoreHeaderSkeleton />
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_: unknown, i: number) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !store) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="text-6xl mb-4">404</div>
        <h1 className="text-xl font-bold text-foreground mb-2">Store Not Found</h1>
        <p className="text-muted-foreground text-center mb-4">
          {error || "The store you're looking for doesn't exist."}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-secondary/30"
    >
      {/* Store Header */}
      <StoreHeader
        store={store}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Products Tab */}
          {activeTab === 'products' && (
            <motion.div
              key="products"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {isLoadingContent ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 8 }).map((_: unknown, i: number) => (
                    <ProductCardSkeleton key={i} />
                  ))}
                </div>
              ) : products.length > 0 ? (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    {products.length} product{products.length !== 1 ? 's' : ''}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {products.map((product, index) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <ProductCard
                          product={{
                            ...product,
                            sellerName: store.name,
                            sellerRating: store.rating,
                            storeSlug: store.slug,
                          }}
                        />
                      </motion.div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 bg-card rounded-lg border border-border">
                  <p className="text-muted-foreground">
                    This store hasn't listed any products yet.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Auctions Tab */}
          {activeTab === 'auctions' && (
            <motion.div
              key="auctions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {isLoadingContent ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 8 }).map((_: unknown, i: number) => (
                    <AuctionCardSkeleton key={i} />
                  ))}
                </div>
              ) : auctions.length > 0 ? (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    {auctions.length} auction{auctions.length !== 1 ? 's' : ''}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {auctions.map((auction, index) => (
                      <motion.div
                        key={auction.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <AuctionCard
                          auction={{
                            ...(auction as Auction),
                            sellerName: store.name,
                            sellerRating: store.rating,
                          }}
                        />
                      </motion.div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 bg-card rounded-lg border border-border">
                  <p className="text-muted-foreground">
                    This store hasn't hosted any auctions yet.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* About Tab */}
          {activeTab === 'about' && (
            <motion.div
              key="about"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="max-w-2xl space-y-6"
            >
              {/* Description */}
              {store.description && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="font-semibold text-foreground mb-2">About</h2>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {store.description}
                  </p>
                </div>
              )}

              {/* Policies */}
              {store.policies && (
                <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                  <h2 className="font-semibold text-foreground">Store Policies</h2>

                  {store.policies.returnPolicy && (
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-medium text-foreground">Return Policy</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {store.policies.returnPolicy}
                        </p>
                      </div>
                    </div>
                  )}

                  {store.policies.shippingPolicy && (
                    <div className="flex items-start gap-3">
                      <Truck className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-medium text-foreground">Shipping Policy</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {store.policies.shippingPolicy}
                        </p>
                      </div>
                    </div>
                  )}

                  {store.policies.paymentMethods && store.policies.paymentMethods.length > 0 && (
                    <div className="flex items-start gap-3">
                      <CreditCard className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-medium text-foreground">Payment Methods</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {store.policies.paymentMethods.join(', ')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Social Links */}
              {store.socialLinks && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="font-semibold text-foreground mb-3">Connect With Us</h2>
                  <div className="flex gap-3">
                    {store.socialLinks.instagram && (
                      <a
                        href={store.socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-md bg-secondary text-secondary-foreground hover:bg-muted transition-colors text-sm"
                      >
                        <Instagram className="w-4 h-4" />
                        Instagram
                      </a>
                    )}
                    {store.socialLinks.facebook && (
                      <a
                        href={store.socialLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-md bg-secondary text-secondary-foreground hover:bg-muted transition-colors text-sm"
                      >
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </a>
                    )}
                    {store.socialLinks.whatsapp && (
                      <a
                        href={`https://wa.me/${store.socialLinks.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-md bg-secondary text-secondary-foreground hover:bg-muted transition-colors text-sm"
                      >
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* No Info Available */}
              {!store.description && !store.policies && !store.socialLinks && (
                <div className="text-center py-12 bg-card rounded-lg border border-border">
                  <p className="text-muted-foreground">
                    This store hasn't added any additional information yet.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </motion.div>
  );
}
